import { config } from '../config';

const CHAT_URL = () => config.protocolKbChatUrl;
const API_URL = () => config.protocolKbApiUrl;

export interface StreamChatOptions {
  prompt: string;
  sessionId: string;
  onChunk: (text: string) => void;
  onDone: () => void;
  onError: (error: Error) => void;
}

export async function streamChat({ prompt, sessionId, onChunk, onDone, onError }: StreamChatOptions): Promise<void> {
  try {
    const response = await fetch(CHAT_URL(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, session_id: sessionId }),
    });

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response stream available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let skipNextData = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        if (trimmed.startsWith('event: ')) {
          skipNextData = true;
          continue;
        }

        if (!trimmed.startsWith('data: ')) continue;

        if (skipNextData) {
          skipNextData = false;
          continue;
        }

        const payload = trimmed.slice(6);
        if (payload === '[DONE]') {
          onDone();
          return;
        }

        let text = payload;
        if (text.startsWith('"') && text.endsWith('"')) {
          text = text.slice(1, -1);
        }
        text = text.replace(/\\n/g, '\n');

        if (text) {
          onChunk(text);
        }
      }
    }

    onDone();
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)));
  }
}

export interface FeedbackPayload {
  session_id: string;
  question: string;
  answer: string;
  rating: number;
  comment?: string;
}

export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  const apiRating = payload.rating === 1 ? 5 : 1;

  const body: Record<string, unknown> = {
    session_id: payload.session_id,
    message_content: `Q: ${payload.question}\n\nA: ${payload.answer}`,
    rating: apiRating,
  };
  if (payload.comment) {
    body.comment = payload.comment;
  }

  const response = await fetch(`${API_URL()}/chat/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Feedback submission failed: ${response.status}`);
  }
}
